import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useVoice } from "../../../contexts/VoiceContext";
import { useContactEditSubmit } from "./useContactEditSubmit";
import CreateContactModal from "../commonSections.jsx/CreateContactModal";
import DeleteModal from "../commonSections.jsx/DeleteContactModal";
import Info from "../commonSections.jsx/Info";
import LastNote from "../commonSections.jsx/LastNote";

/**
 * Mobile version of the contact card. Same 12 fields and 5 action icons
 * as desktop's ContactInfo.jsx, same edit/save behavior (via the shared
 * useContactEditSubmit hook), same 4 reused overlays (Info, LastNote,
 * CreateContactModal, DeleteModal) - only the layout is mobile-specific.
 *
 * Unlike desktop, phone/mobile numbers are plain tel: links here instead
 * of an in-browser Twilio call - a phone already has a native dialer,
 * so there's no need to replicate desktop's click-to-call widget.
 */
const MobileContactCard = ({ contact, clientDetails, callType }) => {
  const { t } = useTranslation();
  const { status } = useVoice();
  const [showModal, setShowModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  const {
    editMode,
    setEditMode,
    contactData,
    editedData,
    errors,
    loading,
    channelOptions,
    sectorOptions,
    handleChange,
    handleEditClick,
    handleSave,
  } = useContactEditSubmit({ contact });

  const togglePanel = (panel) => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  };

  const fields = [
    { label: t("confirm.table.name"), key: "firstName" },
    { label: t("confirm.table.lastName"), key: "lastName", required: true },
    { label: t("dashboard.table.company"), key: "company" },
    { label: t("admin.role"), key: "role" },
    { label: "Sector", key: "industry", span: true },
    { label: t("admin.phone"), key: "phone", isPhone: true },
    { label: t("confirm.table.mobile"), key: "cellphone", isPhone: true },
    { label: t("email"), key: "email", required: true },
    { label: t("confirm.table.country"), key: "country" },
    { label: t("confirm.table.city"), key: "city" },
    { label: t("confirm.table.website"), key: "website" },
    { label: t("agent.Channel"), key: "channel", span: true },
  ];

  const renderFieldValue = (field) => {
    if (editMode) {
      if (field.key === "channel") {
        return (
          <select
            value={editedData[field.key] || ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
            className="w-full text-xs p-1.5 border border-gray-300 rounded"
          >
            <option value="">{t("selectChannel")}</option>
            {channelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }
      if (field.key === "industry") {
        return (
          <select
            value={editedData[field.key] || ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
            className="w-full text-xs p-1.5 border border-gray-300 rounded"
          >
            <option value="">{t("selectSector")}</option>
            {sectorOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }
      return (
        <input
          type="text"
          value={editedData[field.key] || ""}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="w-full text-xs p-1.5 border border-gray-300 rounded"
        />
      );
    }

    if (field.isPhone && contactData[field.key]) {
      return (
        <a
          href={`tel:${contactData[field.key]}`}
          className="text-[11px] font-bold text-secondary truncate block"
        >
          {contactData[field.key]}
        </a>
      );
    }

    return (
      <p className="text-[11px] font-bold text-primary truncate m-0">
        {contactData[field.key] || "-"}
      </p>
    );
  };

  return (
    <div className="flex flex-col gap-2.5 px-4 py-3 bg-secondaryBg">
      <CreateContactModal open={showModal} onClose={() => setShowModal(false)} contact={contactData} />
      <DeleteModal open={deleteModal} onClose={() => setDeleteModal(false)} contact={contactData} />

      <div>
        <p className="text-sm font-extrabold text-primary m-0 truncate">
          {contactData.firstName} {contactData.lastName}
        </p>
        <p className="text-[10px] text-gray-400 m-0 truncate">{contactData.company}</p>
      </div>

      <div className="flex items-center gap-1.5">
        <Info
          clientDetails={clientDetails}
          isOpen={activePanel === "info"}
          onToggle={() => togglePanel("info")}
        />
        <LastNote
          contact={contactData}
          callType={callType}
          isOpen={activePanel === "note"}
          onToggle={() => togglePanel("note")}
        />
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="w-[26px] h-[26px] flex items-center justify-center flex-shrink-0"
          title={t("agent.addMod.head")}
        >
          <svg width="26" height="26" viewBox="0 0 39 38" fill="none">
            <rect x="0.5" y="0.5" width="37.5657" height="36.5366" rx="18.2683" fill="#EBF3FF" stroke="#4CAF50" />
            <path d="M17.7491 16.687C19.7015 16.687 21.2843 15.1901 21.2843 13.3435C21.2843 11.4969 19.7015 10 17.7491 10C15.7966 10 14.2139 11.4969 14.2139 13.3435C14.2139 15.1901 15.7966 16.687 17.7491 16.687Z" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19.8998 25.6011H11V24.8768C11.0118 23.8583 11.31 22.8594 11.8666 21.9729C12.4233 21.0865 13.2203 20.3417 14.1833 19.8079C15.1462 19.2742 16.2436 18.969 17.373 18.9208C18.5023 18.8726 19.6268 19.0831 20.6414 19.5325" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M25.7832 18.9141V26.7156" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21.2842 22.8164H30.2829" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => setDeleteModal(true)}
          className="w-[26px] h-[26px] flex items-center justify-center flex-shrink-0"
          title={t("agent.dltMod.head")}
        >
          <svg width="26" height="26" viewBox="0 0 39 38" fill="none">
            <rect x="0.5" y="0.5" width="37.5657" height="36.5366" rx="18.2683" fill="#EBF3FF" stroke="#F44336" />
            <path d="M19.698 17.6095C21.6357 17.6095 23.2065 16.1299 23.2065 14.3047C23.2065 12.4796 21.6357 11 19.698 11C17.7603 11 16.1895 12.4796 16.1895 14.3047C16.1895 16.1299 17.7603 17.6095 19.698 17.6095Z" stroke="#F44336" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21.8326 26.422H13V25.7061C13.0117 24.6994 13.3076 23.712 13.8601 22.8359C14.4126 21.9598 15.2036 21.2235 16.1592 20.696C17.1149 20.1684 18.204 19.8668 19.3249 19.8192C20.4457 19.7716 21.5617 19.9796 22.5687 20.4238" stroke="#F44336" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M23 24H28" stroke="#F44336" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          onClick={handleEditClick}
          className="w-[26px] h-[26px] flex items-center justify-center flex-shrink-0"
          title={t("button.save")}
        >
          <svg width="26" height="26" viewBox="0 0 37 37" fill="none">
            <rect x="0.5" y="0.5" width="36" height="36" rx="18" fill="#EBF3FF" stroke="#051D42" />
            <path d="M22.7274 11.4142C23.5084 10.6332 24.7748 10.6332 25.5558 11.4142C26.3369 12.1953 26.3369 13.4616 25.5558 14.2426L14.2421 25.5563H11.4137V22.7279L22.7274 11.4142Z" stroke="#0B3C88" />
            <path d="M21.3135 12.8281L24.1419 15.6566" stroke="#0B3C88" />
          </svg>
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-3 grid grid-cols-2 gap-x-2.5 gap-y-2">
        {fields.map((field) => (
          <div key={field.key} className={field.span ? "col-span-2" : ""}>
            <p className="text-[8px] font-extrabold text-gray-400 uppercase tracking-wide m-0 mb-0.5">
              {field.label} {field.required && <span className="text-secondary">*</span>}
            </p>
            {renderFieldValue(field)}
            {errors[field.key] && (
              <p className="text-red-500 text-[10px] mt-0.5 m-0">{errors[field.key]}</p>
            )}
          </div>
        ))}
      </div>

      {editMode && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleEditClick}
            className="flex-1 text-xs font-bold text-primary bg-white border border-gray-300 rounded-lg py-2"
          >
            {t("button.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="flex-1 text-xs font-bold text-white bg-secondary rounded-lg py-2 disabled:opacity-70"
          >
            {t("button.save")}
          </button>
        </div>
      )}

      {status === "dialing" && (
        <p className="text-[10px] text-gray-400 text-center m-0">Dialing...</p>
      )}
    </div>
  );
};

export default MobileContactCard;